import imageUrlBuilder from '@sanity/image-url'
import type { SanityImageSource, SanityProjectDetails } from '@sanity/image-url/lib/types/types'
import type { MaybeRefOrGetter } from 'vue'

// Accepts a ref/getter so the head stays in sync with lazily fetched data.
// Must be called during setup (not inside a watcher) for the tags to be server rendered.
export function useSanitySeo(seoInput?: MaybeRefOrGetter<Seo | undefined | null>) {
  // Get the Nuxt runtime config for site settings
  const config = useRuntimeConfig()
  const siteUrl = (config.public.siteUrl || useRequestURL().origin).replace(/\/$/, '')
  const siteName = config.public.siteName || 'Nitex'

  // Get the Sanity configuration from the `useSanity` composable
  const { config: sanityConfig } = useSanity()

  // Create the URL builder using the provided config
  const builder = imageUrlBuilder(sanityConfig as SanityProjectDetails)

  // Helper function to generate image URLs
  function urlFor(source: SanityImageSource) {
    return builder.image(source).auto('format')
  }

  const route = useRoute()

  const seo = computed(() => toValue(seoInput) || undefined)

  // Use the URL builder to create the Open Graph image URL
  const ogImageUrl = computed(() => {
    const image = seo.value?.image
    if (!image?.asset) return null

    try {
      return urlFor(image as SanityImageSource)
        .width(1200)
        .height(630)
        .fit('crop')
        .crop('center')
        .url()
    } catch (error) {
      console.error('Error generating OG image URL:', error)
      return null
    }
  })

  const metaTitle = computed(() => seo.value?.title)
  const metaDescription = computed(() => seo.value?.description)

  // Helper to validate description
  const isValidDescription = (desc: string | null | undefined): boolean => {
    if (!desc) return false
    if (desc === '%site.description') return false
    if (desc === 'null') return false
    if (desc === 'undefined') return false
    if (desc.includes('%')) return false // Catch unknown other template variables
    if (desc.trim() === '') return false
    return true
  }

  useHead(() => {
    if (!seo.value) return {}

    // Format the Open Graph title to include the site name, similar to the page title
    const ogTitle = metaTitle.value ? `${metaTitle.value} | ${siteName}` : siteName
    const description = isValidDescription(metaDescription.value) ? metaDescription.value : null

    // Construct canonical URL using the site URL and current path
    // If a specific canonical URL is provided in the SEO data, use that instead
    const path = route.path
    const canonicalUrl = seo.value.canonical || (path ? `${siteUrl}${path}` : siteUrl)

    const image = ogImageUrl.value

    return {
      title: metaTitle.value,
      meta: [
        // Standard meta tags (null explicitly overrides defaults)
        { name: 'description', content: description },
        { name: 'robots', content: seo.value.noindex ? 'noindex' : 'all' },

        // Open Graph meta tags
        { property: 'og:title', content: ogTitle },
        { property: 'og:description', content: description },
        ...(image
          ? [
              { property: 'og:image', content: image },
              { property: 'og:image:width', content: '1200' },
              { property: 'og:image:height', content: '630' },
            ]
          : []),
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: siteName },
        { property: 'og:locale', content: 'da' },

        // Twitter Card meta tags
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: ogTitle },
        { name: 'twitter:description', content: description },
        ...(image ? [{ name: 'twitter:image', content: image }] : []),
      ],
      link: [{ rel: 'canonical', href: canonicalUrl }],
    }
  })

  // Return computed values for use in your component
  return {
    metaTitle,
    metaDescription,
    ogImageUrl,
  }
}
